/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography2, useTheme } from '@coursera/cds-core';

import A11yScreenReaderOnly from 'bundles/a11y/components/A11yScreenReaderOnly';

import _t from 'i18n!nls/program-home';

const progressBanner = (theme: Theme) =>
  css({
    margin: theme.spacing(16, 0, 32, 0),
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 0,
      margin: theme.spacing(12, 0, 32),
    },
  });

const progressBar = (theme: Theme) =>
  css({
    width: '216px',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '10px',
    margin: 0,
    top: '160px',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '180px',
    },
  });

const greyBar = (theme: Theme) =>
  css({
    width: '100%',
    height: '10px',
    background: theme.palette.gray[300],
    borderRadius: '10px',
  });

const fullProgressBar = (theme: Theme) =>
  css({
    width: '100%',
    height: '10px',
    background: theme.palette.blue[600],
    borderRadius: '10px',
  });

const blueBar = (maxWidthAsPercentage: string, theme: Theme) =>
  css({
    height: '10px',
    background: theme.palette.blue[600],
    display: 'block',
    borderRadius: '10px 0 0 10px',
    width: `${maxWidthAsPercentage}%`,
  });

export type ProgressBarProps = {
  progressPercentage: string;
  currentStep: number;
  totalSteps: number;
  currentStepName: string;
};

export const ProgressBar = ({ progressPercentage, currentStep, totalSteps, currentStepName }: ProgressBarProps) => {
  const theme = useTheme();

  return (
    <Grid container css={progressBanner(theme)} data-testid="progress-bar">
      <Grid>
        <Typography2 component="p" align="center">
          {_t(`Step #{currentStep} of #{totalSteps}: #{currentStepName}`, { currentStep, totalSteps, currentStepName })}
        </Typography2>
        <Grid item className="progress-bar" data-testid="onboarding-progress-bar" zeroMinWidth css={progressBar(theme)}>
          {progressPercentage !== '0' && (
            <A11yScreenReaderOnly tagName="span">{`${progressPercentage}% ${_t('completed')}`}</A11yScreenReaderOnly>
          )}
          <div css={greyBar(theme)}>
            <div css={progressPercentage === '100' ? fullProgressBar(theme) : blueBar(progressPercentage, theme)} />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
